import type { DynamicCommandItem } from "@v2/hooks/useDynamicCommandMenuItems";
import type { EditorCanvas } from "replo-utils/lib/misc/canvas";

import * as React from "react";

import { commandItemHasGlobalCommand } from "@replo/design-system/utils/commands";
import { setActiveCanvas } from "@v1/components/canvas/canvas-reducer";
import {
  allElementTypes,
  elementTypeToEditorData,
} from "@v1/components/editor/element";
import { useCreateElement } from "@v1/hooks/element/useCreateElement";
import { useElementsMetadataByType } from "@v1/hooks/useElementsMetadataByType";
import { useGlobalEditorActions } from "@v1/hooks/useGlobalEditorActions";
import useSetDraftElement from "@v1/hooks/useSetDraftElement";
import { selectCommandMenuItems } from "@v1/reducers/core-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { useDynamicCommandMenuItems } from "@v2/hooks/useDynamicCommandMenuItems";
import { routes } from "@v2/utils/router";
import { Eye } from "lucide-react";
import { generatePath, useNavigate } from "react-router-dom";

/**
 * Provider component that registers all v1-specific command menu items.
 * This component should only be mounted within the v1 EditorApp.
 */
export function V1CommandMenuItemsProvider() {
  useV1BaseCommandItems();
  useV1CreateMenuItems();
  useV1NavigateMenuItems();
  useV1DeviceSizeMenuItems();

  return null;
}

/**
 * Registers base command menu items from Redux (edit, view, text groups)
 */
function useV1BaseCommandItems() {
  const commandMenuItems = useEditorSelector(selectCommandMenuItems);
  const globalActions = useGlobalEditorActions();

  const items = React.useMemo(() => {
    const result: Record<string, DynamicCommandItem> = {};

    commandMenuItems.forEach((group) => {
      Object.entries(group.items).forEach(([key, item]) => {
        // Check if this item has a global command
        if (commandItemHasGlobalCommand(item)) {
          const itemKey = `v1-base-${group.id}-${key}`;
          result[itemKey] = {
            ...item,
            group: group.id,
            onSelect: () => {
              globalActions[item.command]();
            },
          };
        }
      });
    });

    return result;
  }, [commandMenuItems, globalActions]);

  useDynamicCommandMenuItems(items);
}

/**
 * Registers create menu items (Create blank page, etc.)
 */
function useV1CreateMenuItems() {
  const { handleCreateElement } = useCreateElement();

  const items = React.useMemo(() => {
    const result: Record<string, DynamicCommandItem> = {};

    for (const type of allElementTypes) {
      const key = `v1-create-${type}-blank`;
      result[key] = {
        label: `Create blank ${elementTypeToEditorData[type].singularDisplayName.toLowerCase()}`,
        group: "create",
        icon: { type: "create" },
        onSelect: () => {
          void handleCreateElement({ type });
        },
      };
    }

    return result;
  }, [handleCreateElement]);

  useDynamicCommandMenuItems(items);
}

/**
 * Registers navigate menu items (Navigate to... pages)
 */
function useV1NavigateMenuItems() {
  const navigate = useNavigate();
  const { project } = useCurrentProjectContext();
  const setDraftElement = useSetDraftElement();
  const projectId = project?.id;
  const pages = useElementsMetadataByType("page");

  const items = React.useMemo(() => {
    const result: Record<string, DynamicCommandItem> = {};

    pages.forEach((page) => {
      const key = `v1-navigate-${page.id}`;
      result[key] = {
        label: page.name,
        group: "navigate",
        icon: {
          type: "publishedStatus",
          isPublished: page.isPublished ?? false,
        },
        onSelect: () => {
          setDraftElement({
            componentIds: [],
          });
          navigate(
            generatePath(routes.editor.element, {
              projectId,
              elementId: page.id,
            }),
          );
        },
      };
    });

    return result;
  }, [pages, navigate, projectId, setDraftElement]);

  useDynamicCommandMenuItems(items);
}

const DEVICE_CONFIGS: {
  canvas: EditorCanvas;
  label: string;
}[] = [
  { canvas: "desktop", label: "Select Device Size: Desktop" },
  { canvas: "mobile", label: "Select Device Size: Mobile" },
  { canvas: "tablet", label: "Select Device Size: Tablet" },
];

/**
 * Registers device size switching command menu items.
 * Allows switching between Desktop, Mobile, and Tablet views via cmd-K menu.
 *
 * The setActiveCanvas action automatically:
 * 1. Makes the canvas visible (sets isVisible = true)
 * 2. Sets it as the active canvas
 * 3. Preserves selected element (when using source: "cmd-k")
 * 4. Preserves scroll position (when using source: "cmd-k")
 */
function useV1DeviceSizeMenuItems() {
  const dispatch = useEditorDispatch();

  const items = React.useMemo(() => {
    const result: Record<string, DynamicCommandItem> = {};

    DEVICE_CONFIGS.forEach(({ canvas, label }) => {
      const key = `device-size-${canvas}`;
      result[key] = {
        label,
        group: "view",
        icon: { type: "iconComponent", component: Eye },
        onSelect: () => {
          // Note (Noah, 2025-01-22): setActiveCanvas handles making the canvas
          // visible and setting it as active. We use source: "cmd-k" to
          // preserve the scroll position and NOT reset the frame position,
          // which keeps the user's view context intact. This source also
          // doesn't deselect the selected element, which is the key feature.
          dispatch(
            setActiveCanvas({
              canvas,
              isRightBarVisible: true,
              source: "cmd-k",
            }),
          );
        },
      };
    });

    return result;
  }, [dispatch]);

  useDynamicCommandMenuItems(items);
}
